import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { itemNav } from './practiceMeta';

import type { PracticeItem } from '@/lib/api/lms';

/** Wraps children in a router Link pointing at the right destination for a
 *  practice item (problem solver / quiz / assignment-project detail / topic). */
export function PracticeItemLink({
  item,
  batchId,
  className,
  children,
}: {
  item: PracticeItem;
  batchId: string;
  className?: string;
  children: ReactNode;
}) {
  const nav = itemNav(item);
  switch (nav.kind) {
    case 'problem':
      return (
        // batchId is what the workspace resolves "next problem" against.
        // Passing nothing here is why a student who opened a problem from
        // Practice got prev/next over the whole global catalogue instead of
        // the set they were working through.
        //
        // batchId and NOT topicId on purpose: topicId would win precedence and
        // sequence them through the owning topic's assessments, but someone in
        // the Practice list is working a weekly set — the ladder that set is
        // authored as is the sequence they actually see in front of them.
        <Link
          to="/problems/$slug"
          params={{ slug: nav.slug }}
          search={{ batchId }}
          className={className}
        >
          {children}
        </Link>
      );
    case 'quiz':
      return (
        <Link
          to="/courses/$batchId/topics/$topicId/quiz/$assessmentId"
          params={{ batchId, topicId: nav.topicId, assessmentId: nav.assessmentId }}
          // Records that this attempt was launched from the Practice /
          // Assessments list, so the quiz's exit says "Assessments" rather
          // than dropping the student into a topic they did not pick.
          search={{ from: 'practice' as const }}
          className={className}
        >
          {children}
        </Link>
      );
    case 'detail':
      return (
        <Link
          to="/courses/$batchId/practice/$category/$assessmentId"
          params={{ batchId, category: nav.category, assessmentId: nav.assessmentId }}
          className={className}
        >
          {children}
        </Link>
      );
    case 'topic':
      return (
        <Link
          to="/courses/$batchId/topics/$topicId"
          params={{ batchId, topicId: nav.topicId }}
          className={className}
        >
          {children}
        </Link>
      );
  }
}
